/* Created by Interactive 63 - https://interactive63.com */
/**
 * Limits and defaults for the GIF and MP4 export settings, shared by the project file
 * validation and the export dialog so both agree on what a valid value is.
 */
import type { ExportSettings, MediaSettings } from './types';

export const MEDIA_MIN_WIDTH = 16;
export const MEDIA_MAX_WIDTH = 4096;
export const MEDIA_MAX_PLAYS = 20;

/** Frame rates offered for each format. GIF delays are whole hundredths of a second. */
export const GIF_FRAME_RATES = [10, 15, 20, 25, 30] as const;
export const MP4_FRAME_RATES = [24, 30, 60] as const;

/** What a video is drawn on when the artboard is transparent, since video cannot be. */
export const VIDEO_FALLBACK_BACKGROUND = '#ffffff';

/** The default GIF and MP4 settings. */
export function defaultMediaSettings(): MediaSettings {
  return {
    width: 720,
    fps: 30,
    loop: true,
    plays: 1,
  };
}

/** Whether a string is a six digit hex colour such as "#1a2b3c". */
export function isHexColour(value: string): boolean {
  return /^#[0-9a-f]{6}$/i.test(value);
}

/** Pulls a width into the supported range, rounded to a whole pixel. */
export function clampMediaWidth(width: number): number {
  return Math.min(MEDIA_MAX_WIDTH, Math.max(MEDIA_MIN_WIDTH, Math.round(width)));
}

/** The frame rate to use for a format: the saved one if the format offers it, otherwise the default. */
export function offeredFrameRate(format: 'gif' | 'mp4', fps: number): number {
  const rates: readonly number[] = format === 'gif' ? GIF_FRAME_RATES : MP4_FRAME_RATES;
  return rates.includes(fps) ? fps : defaultMediaSettings().fps;
}

/** Whether an export format is one of the two rendered ones, GIF and MP4. */
export function isMediaFormat(format: ExportSettings['format']): format is 'gif' | 'mp4' {
  return format === 'gif' || format === 'mp4';
}
